import { areMathAnswersEquivalent } from './mathAnswer.ts';
import type { MarkVerdict } from './markDisplay.ts';

export type StepFeedbackStatus = 'correct' | 'wrong' | 'neutral';

export type PartFeedback = {
  status: StepFeedbackStatus;
  comment?: string;
};

export type WorkingAnswerFeedback = {
  steps: PartFeedback[];
  final: PartFeedback;
};

const splitWorkedLines = (workedSolution?: string | null): string[] => {
  if (!workedSolution?.trim()) return [];
  return workedSolution
    .split(/\n+/)
    .map((line) => line.replace(/^\s*(?:step\s*\d+\s*[:.)-]\s*)?/i, '').trim())
    .filter(Boolean);
};

const equivalent = (a: string, b: string): boolean =>
  a.trim() !== '' && b.trim() !== '' && areMathAnswersEquivalent(a.trim(), b.trim());

const evaluateFinalFeedback = (
  finalAnswer: string,
  expected: string,
  verdict: MarkVerdict | null,
  detailFeedback?: string,
): PartFeedback => {
  const trimmed = finalAnswer.trim();
  if (!trimmed) {
    return { status: 'neutral', comment: 'No final answer entered.' };
  }

  if (verdict === 'correct' || (expected && equivalent(trimmed, expected))) {
    return { status: 'correct' };
  }

  if (verdict === 'partial') {
    return {
      status: 'neutral',
      comment: detailFeedback?.trim() || 'Close, but not fully correct.',
    };
  }

  return {
    status: 'wrong',
    comment: detailFeedback?.trim() || 'Does not match the expected answer.',
  };
};

const evaluateStepFeedback = (step: string, modelLine?: string): PartFeedback => {
  if (!step.trim()) {
    return { status: 'neutral' };
  }
  if (modelLine && equivalent(step, modelLine)) {
    return { status: 'correct' };
  }
  return {
    status: 'neutral',
    ...(modelLine ? { comment: 'Does not match this model step.' } : {}),
  };
};

export const evaluateWorkingAnswerFeedback = (params: {
  workingSteps: string[];
  finalAnswer: string;
  expectedAnswer: string;
  workedSolution?: string | null;
  finalVerdict: MarkVerdict | 'unanswered' | 'checking' | null;
  finalDetailFeedback?: string;
}): WorkingAnswerFeedback | null => {
  const steps = params.workingSteps.map((s) => s.trim()).filter(Boolean);
  const finalAnswer = params.finalAnswer.trim();
  if (!steps.length && !finalAnswer) return null;
  if (params.finalVerdict === 'checking') return null;

  const expected = params.expectedAnswer.trim();
  const workedLines = splitWorkedLines(params.workedSolution);
  const verdictForFinal =
    params.finalVerdict === 'unanswered' || params.finalVerdict === 'checking'
      ? null
      : params.finalVerdict;

  return {
    steps: steps.map((step, index) => evaluateStepFeedback(step, workedLines[index])),
    final: evaluateFinalFeedback(finalAnswer, expected, verdictForFinal, params.finalDetailFeedback),
  };
};

const sanitizeAiStatus = (value: unknown): StepFeedbackStatus => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (raw === 'correct' || raw === 'wrong' || raw === 'neutral') return raw;
  return 'neutral';
};

const parseAiPartItem = (value: unknown): PartFeedback | null => {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const status = sanitizeAiStatus(row.status);
  const comment = typeof row.comment === 'string' ? row.comment.trim() : '';
  if (status === 'correct' && !comment) return { status };
  return { status, ...(comment ? { comment } : {}) };
};

/** Parse part_feedback from shadow_mark_submission / AI marker JSON. */
export const parseAiPartFeedback = (raw: unknown): WorkingAnswerFeedback | null => {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const rawSteps = Array.isArray(row.steps) ? row.steps : [];
  const steps = rawSteps
    .map((step) => parseAiPartItem(step))
    .filter((step): step is PartFeedback => step != null);
  const final = parseAiPartItem(row.final);
  if (!final && !steps.length) return null;
  return {
    steps,
    final: final ?? { status: 'neutral', comment: 'Not reviewed.' },
  };
};
